// Reconciles pre-v2 tool attribution with the current scheme.
//
// Free-tool hand-offs used to write the widget's own label into utm_medium
// ("cornell_save", "mindmap_save", ...), which meant ~35 distinct mediums and
// no way to group tool traffic or to line a tool up with its /tools/<slug>
// page. They now use the same shape as the blog:
//
//   utm_source=marketing  utm_medium=tool  utm_campaign=<slug>  utm_content=<placement>
//
// Rewriting the links only fixes traffic from here on. The first-touch cookie
// lives for 400 days, so a visitor who met a tool before the change still
// carries the old shape and would sign up months from now under a medium that
// nothing else reports — the old and new data would never form one series.
// Normalising on read closes that gap for every such visitor.
//
// This is deliberately one-way (legacy -> current) and additive: anything that
// already matches the current scheme passes through untouched.

/** Legacy utm_medium value -> the tool slug it belonged to. */
const LEGACY_MEDIUM_TO_SLUG: Record<string, string> = {
  ai_auto_tagger_save: 'ai-auto-tagger',
  autotagger_save: 'ai-auto-tagger',
  audio_transcriber_save: 'audio-transcriber',
  background_remover_save: 'background-remover',
  bmc_save: 'business-model-canvas',
  braindump_save: 'brain-dump-organizer',
  conceptmap_save: 'concept-map-maker',
  cornell_save: 'cornell-notes',
  eisenhower_save: 'eisenhower-matrix',
  empathy_map_save: 'empathy-map',
  excalidraw_viewer_save: 'excalidraw-viewer',
  fishbone_save: 'fishbone-diagram',
  five_whys_save: 'five-whys',
  flashcards_save: 'flashcard-maker',
  handwriting_to_text_save: 'handwriting-to-text',
  image_to_text_save: 'image-to-text',
  journey_map_save: 'customer-journey-map',
  jsoncanvas_save: 'json-canvas-viewer',
  kanban_save: 'kanban-board',
  lean_canvas_save: 'lean-canvas',
  markdown_editor_save: 'markdown-editor',
  meeting_notes_save: 'ai-meeting-notes',
  mindmap_save: 'mind-map-generator',
  notepad_save: 'online-notepad',
  org_chart_save: 'org-chart',
  pdf_to_text_save: 'pdf-to-text',
  pdf_visual_outline_save: 'pdf-visual-outline',
  retro_save: 'retrospective-board',
  semantic_clusterer_save: 'semantic-note-clusterer',
  soap_save: 'soap-note-generator',
  swot_save: 'swot-analysis',
  timeline_save: 'timeline-maker',
  venn_save: 'venn-diagram',
  whiteboard_photo_save: 'whiteboard-photo-to-canvas',
  whiteboard_save: 'whiteboard',
  word_counter_save: 'word-counter',
}

/** Campaigns that were hand-written before the slug became the campaign. */
const LEGACY_CAMPAIGN_ALIASES: Record<string, string> = {
  'app-finder': 'canvas-app-finder',
}

/**
 * Recover the tool slug from the landing page of a first touch that predates
 * per-tool campaigns. `/tools/<slug>` identifies the tool exactly; the bare
 * hub gets the same campaign its CTA writes today.
 */
function slugFromLandingPage(landingPage: unknown): string | undefined {
  if (typeof landingPage !== 'string') return undefined
  const path = landingPage.split('?')[0].replace(/\/+$/, '')
  if (path === '/tools') return 'tools-index'
  const m = /^\/tools\/([a-z0-9-]+)$/.exec(path)
  return m ? m[1] : undefined
}

/**
 * Map a stored attribution payload onto the current scheme, in place of the
 * shape it was written with. Returns a new object; the input is not mutated.
 */
export function normalizeLegacyAttribution(
  attr: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...attr }
  const medium = typeof out.utm_medium === 'string' ? out.utm_medium : undefined

  // 1. The save-bar hand-offs, whose label was the medium.
  const mappedSlug = medium ? LEGACY_MEDIUM_TO_SLUG[medium] : undefined
  if (mappedSlug) {
    out.utm_medium = 'tool'
    if (!out.utm_campaign) out.utm_campaign = mappedSlug
    // These were only ever emitted by the save bar, so the placement is known.
    if (!out.utm_content) out.utm_content = 'save_to_canvas'
    return out
  }

  // 2. Tool traffic that already used medium=tool but carried no campaign.
  //    The landing page still says which tool it was; the placement does not
  //    survive, so utm_content is left unset rather than guessed.
  if (medium === 'tool' && !out.utm_campaign) {
    const recovered = slugFromLandingPage(out.landing_page)
    if (recovered) out.utm_campaign = recovered
    return out
  }

  // 3. Campaign names that predate the slug convention.
  if (typeof out.utm_campaign === 'string') {
    const alias = LEGACY_CAMPAIGN_ALIASES[out.utm_campaign]
    if (alias) out.utm_campaign = alias
  }
  return out
}

/** Exported for tests and for any server-side backfill of stored events. */
export const LEGACY_ATTRIBUTION_MEDIUMS = LEGACY_MEDIUM_TO_SLUG
